import React, { useState, useEffect } from 'react';
import { Heart, Quote, ChevronLeft, ChevronRight, Star, Users } from 'lucide-react';
import ImageWithFallback from './ImageWithFallback';
import SubscriptionModal from './SubscriptionModal';

const Testimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);

  // Témoignages touchants de couples FSF
  const testimonials = [
    {
      id: 1,
      name: "Marie & Jean-Baptiste ADJOVI",
      location: "Cotonou, Bénin",
      years: "12 ans de mariage",
      image: "/assets/images/couple_enfant.png",
      fallbackType: "couple",
      testimonial: "Grâce à la communauté FSF, nous avons appris à prier ensemble chaque jour. Nos enfants grandissent dans l'amour du Christ et la tradition béninoise. Cette communauté nous a sauvé notre mariage lors des moments difficiles.",
      highlight: "La prière familiale quotidienne a transformé notre foyer",
      children: 3,
      rating: 5
    },
    {
      id: 2,
      name: "Estelle & Rodrigue GBAGUIDI", 
      location: "Porto-Novo, Bénin",
      years: "8 ans de mariage",
      image: "/assets/images/couple.png",
      fallbackType: "family",
      testimonial: "FSF nous a aidés à comprendre que le mariage est une vocation sainte. Les retraites de couples ont renforcé notre amour. Nous témoignons que Dieu bénit les familles qui le mettent au centre.",
      highlight: "Notre amour grandit chaque jour grâce aux enseignements reçus",
      children: 2,
      rating: 5
    },
    {
      id: 3,
      name: "Chantal & Pierre HOUNNOU",
      location: "Calavi, Bénin", 
      years: "15 ans de mariage",
      image: "/assets/images/couple.png",
      fallbackType: "couple",
      testimonial: "Après 5 ans de stérilité, nous avons rejoint FSF et découvert la prière de neuvaine. Dieu nous a bénis avec 4 beaux enfants. Cette communauté est notre seconde famille.",
      highlight: "Dieu a exaucé nos prières pour la fécondité",
      children: 4,
      rating: 5
    },
    {
      id: 4,
      name: "Sylvie & Constant AGBO",
      location: "Ouidah, Bénin",
      years: "6 ans de mariage",
      image: "/assets/images/famille.png",
      fallbackType: "family",
      testimonial: "Jeunes mariés, nous étions perdus. FSF nous a enseigné les valeurs chrétiennes du mariage béninois. Nos parents sont fiers de voir notre foyer uni dans la foi et les traditions.",
      highlight: "L'harmonie entre foi chrétienne et culture béninoise",
      children: 1,
      rating: 5
    }
    
  ];

  // Changement automatique des témoignages
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 7000); // Change toutes les 7 secondes
    
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const currentData = testimonials[currentTestimonial];

  return (
    <section id="temoignages" className="py-2 bg-gradient-to-br from-blue-50 via-amber-50 to-pink-50 relative overflow-hidden">
      {/* Éléments décoratifs */}
      <div className="absolute top-10 left-10 text-blue-200 opacity-30">
        <Heart className="w-20 h-20 animate-pulse" />
      </div>
      <div className="absolute bottom-10 right-10 text-pink-200 opacity-30">
        <Heart className="w-16 h-16 animate-pulse" style={{animationDelay: '1s'}} />
      </div>
      <div className="absolute top-1/2 left-20 text-amber-200 opacity-20">
        <Users className="w-24 h-24 animate-bounce" style={{animationDelay: '2s'}} />
      </div>

      <div className="container mx-auto px-4">
        {/* En-tête de section */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Heart className="w-8 h-8 text-red-500 animate-heart-beat" />
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 font-serif">
              Témoignages de nos couples
            </h2>
            <Heart className="w-8 h-8 text-red-500 animate-heart-beat" />
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto text-justify">
            Découvrez comment la communauté FSF transforme la vie des couples mariés au Bénin.
            Des histoires vraies d'amour, de foi et de réconciliation.
          </p>
          <div className="flex items-center justify-center space-x-2 mt-4">
            <Star className="w-5 h-5 text-yellow-500 fill-current" />
            <Star className="w-5 h-5 text-yellow-500 fill-current" />
            <Star className="w-5 h-5 text-yellow-500 fill-current" />
            <Star className="w-5 h-5 text-yellow-500 fill-current" />
            <Star className="w-5 h-5 text-yellow-500 fill-current" />
            <span className="text-gray-600 ml-2 font-medium">Plus de 150 couples témoignent</span>
          </div>
        </div>

        {/* Slider de témoignages */}
        <div className="relative max-w-6xl mx-auto">
          {/* Témoignage principal */}
          <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-amber-100">
            <div className="md:flex">
              {/* Image du couple */}
              <div className="md:w-2/5 relative">
                <div className="h-80 md:h-96 relative overflow-hidden">
                  <ImageWithFallback
                    src={currentData.image}
                    fallbackType={currentData.fallbackType}
                    alt={`Couple ${currentData.name}`}
                    className="w-full h-full object-cover"
                    index={currentTestimonial}
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-amber-900/40 via-transparent to-transparent"></div>
                  
                  {/* Informations sur l'image */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 border border-white/50">
                      <h3 className="font-bold text-gray-800 text-lg">{currentData.name}</h3>
                      <div className="flex items-center justify-between text-sm text-gray-600 mt-1">
                        <span className="flex items-center space-x-1">
                          <span>📍</span>
                          <span>{currentData.location}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Heart className="w-4 h-4 text-red-500" />
                          <span>{currentData.years}</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex">
                          {Array.from({length: currentData.rating}).map((_, i) => (
                            <Star key={i} className="w-4 h-4 text-yellow-500 fill-current" />
                          ))}
                        </div>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                          {currentData.children} enfant{currentData.children > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contenu du témoignage */}
              <div className="md:w-3/5 p-8 md:p-12">
                <div className="h-full flex flex-col justify-center">
                  {/* Citation */}
                  <div className="mb-6">
                    <Quote className="w-12 h-12 text-amber-300 mb-4" />
                    <blockquote className="text-xl md:text-2xl text-gray-800 leading-relaxed font-medium mb-6 italic">
                      "{currentData.testimonial}"
                    </blockquote>
                  </div>

                  {/* Phrase marquante */}
                  <div className="bg-gradient-to-r from-amber-100 via-pink-50 to-blue-100 rounded-2xl p-6 mb-6 border-l-4 border-amber-500">
                    <div className="flex items-start space-x-3">
                      <Heart className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
                      <p className="text-lg font-semibold text-amber-700 italic text-justify">
                        {currentData.highlight}
                      </p>
                    </div>
                  </div>

                  {/* Statistiques du couple */}
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-blue-50 rounded-xl p-3">
                      <div className="text-2xl font-bold text-blue-600">{currentData.children}</div>
                      <div className="text-sm text-gray-600">Enfants</div>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-3">
                      <div className="text-2xl font-bold text-amber-500">{currentData.years.split(' ')[0]}</div>
                      <div className="text-sm text-gray-600">Années</div>
                    </div>
                    <div className="bg-pink-50 rounded-xl p-3">
                      <div className="text-2xl font-bold text-pink-600">FSF</div>
                      <div className="text-sm text-gray-600">Membre</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contrôles de navigation */}
          <div className="absolute top-1/2 -left-6 transform -translate-y-1/2 z-10">
            <button
              onClick={prevTestimonial}
              className="bg-white shadow-lg hover:shadow-xl text-amber-500 p-3 rounded-full transition-all duration-300 hover:bg-amber-50 border-2 border-amber-100"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          </div>
          <div className="absolute top-1/2 -right-6 transform -translate-y-1/2 z-10">
            <button
              onClick={nextTestimonial}
              className="bg-white shadow-lg hover:shadow-xl text-amber-500 p-3 rounded-full transition-all duration-300 hover:bg-amber-50 border-2 border-amber-100"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Indicateurs */}
          <div className="flex justify-center space-x-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentTestimonial 
                    ? 'w-12 h-3 bg-amber-500' 
                    : 'w-3 h-3 bg-gray-300 hover:bg-amber-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Section statistiques */}
        <div className="mt-16 bg-white rounded-3xl shadow-xl p-8 max-w-4xl mx-auto border-2 border-amber-100">
          <h3 className="text-2xl font-bold text-center text-gray-800 mb-8 font-serif">
            Impact de notre communauté
          </h3>
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div className="space-y-2">
              <div className="text-3xl font-bold text-amber-500">+150</div>
              <div className="text-gray-600">Couples accompagnés</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-blue-600">95%</div>
              <div className="text-gray-600">Mariages sauvés</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-green-600">+300</div>
              <div className="text-gray-600">Enfants bénis</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-red-600">+15</div>
              <div className="text-gray-600">Années de service</div>
            </div>
          </div>
        </div>

        {/* Appel à l'action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-amber-500 to-pink-600 rounded-3xl shadow-2xl p-8 max-w-3xl mx-auto text-white">
            <h3 className="text-3xl font-bold mb-4 font-serif">
              Vous aussi, rejoignez notre famille !
            </h3>
            <p className="text-xl mb-6 opacity-90 text-justify">
              Vivez une expérience transformante pour votre couple et votre famille
            </p>
            {/* <button 
              onClick={() => setIsSubscriptionModalOpen(true)}
              className="bg-white text-amber-500 px-8 py-3 rounded-xl font-bold text-lg hover:bg-amber-50 transition-colors duration-300 shadow-lg"
            >
              Découvrir comment nous rejoindre
            </button> */}
          </div>
        </div>
      </div>

      {/* Modal d'inscription */}
      <SubscriptionModal 
        isOpen={isSubscriptionModalOpen} 
        onClose={() => setIsSubscriptionModalOpen(false)} 
      />
    </section>
  );
};

export default Testimonials;